<template>
  <fragment>
    <AppBody
      title="Relatórios Sintéticos"
      subtitle="Vendas por Forma de Pagamento"
      :actions="actions"
      :primaryLoading="exportCSVLoading"
      :secondaryLoading="exportPDFLoading"
    >
      <CardModule>
        <v-form
          @submit.prevent="filterPaymentMethod"
          class="search-forms"
          ref="form"
        >
          <v-row>
            <v-col cols="4">
              <BaseSelect
                label="Forma de Pagamento"
                :items="paymentMethods"
                v-model="filter.paymentMethod"
                item-text="key"
                item-value="value"
                placeholder="Selecione a forma de pagamento"
                clearable
              />
            </v-col>
            <v-col cols="4">
              <BaseSelect
                label="Intervalo de Datas"
                :items="intervalDates"
                item-text="text"
                item-value="value"
                v-model="filter.dateInterval"
                placeholder="Selecione o intervalo de datas"
              />
            </v-col>
            <v-col cols="4" v-if="isNotCustomRangeDates">
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <BaseTextField
                    label="Datas"
                    v-model="dateRangeText"
                    :append-icon="$config.icons.calendar"
                    readonly
                    :disabled="disabledDatePicker"
                    v-bind="attrs"
                    v-on="on"
                    :placeholder="placeholderData"
                    :color="$config.dados.corinputs"
                    clearable
                    @click:clear="filter.rangeDates = []"
                  >
                  </BaseTextField>
                </template>
                <v-date-picker
                  v-model="computeRange"
                  @input="menu = false"
                  :active-picker.sync="activePicker"
                  @change="computeRange"
                  locale="pt"
                  :header-color="$config.dados.corinputs"
                  @click:month="monthEvent"
                  :color="$config.dados.corinputs"
                  range
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="4" v-else>
              <v-menu
                tabindex="10"
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <BaseTextField
                    label="Datas de uso"
                    v-model="dateRangeText"
                    readonly
                    :append-icon="$config.icons.calendar"
                    v-bind="attrs"
                    v-on="on"
                    :placeholder="placeholderData"
                    :color="$config.dados.corinputs"
                    clearable
                    @click:clear="filter.rangeDates = []"
                  ></BaseTextField>
                </template>
                <v-date-picker
                  v-model="filter.rangeDates"
                  :active-picker.sync="activePickerOrders"
                  @change="filter.rangeDates"
                  locale="pt"
                  @input="
                    filter.rangeDates.length == 2
                      ? (menu = false)
                      : (menu = true)
                  "
                  :header-color="$config.dados.corinputs"
                  landscape
                  :color="$config.dados.corinputs"
                  range
                >
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col class="d-flex justify-end">
              <BaseButton
                tabindex="13"
                variant="filter"
                class="button rounded-lg"
                type="submit"
              >
                Filtrar
              </BaseButton>
            </v-col>
          </v-row>
        </v-form>
        <v-data-table
          class="rounded-lg"
          no-data-text="Não há registro de vendas por forma de pagamento."
          loading-text="Buscando informações..."
          :loading="salesByPaymentMethodLoading"
          :headers="headers"
          :items="allSalesByPaymentMethod.info"
          :items-per-page="10"
          :header-props="{
            class: 'rounded-t-lg',
            color: $config.dados.corinputs,
          }"
          :footer-props="{
            class: 'rounded-b-lg',
            itemsPerPageOptions: [10, 20, 50],
            itemsPerPageText: 'Vendas por página:',
          }"
        >
          <template v-slot:item.desc_forma_pag="{ item }">
            {{ metodoPagamentoExibicao(item.desc_forma_pag) }}
          </template>
          <template v-slot:item.valor="{ item }">
            {{ item.valor | moeda }}
          </template>
          <template v-slot:item.valor_juros="{ item }">
            {{ item.valor_juros | moeda }}
          </template>
          <template v-slot:item.valor_cancelado="{ item }">
            {{ item.valor_cancelado | moeda }}
          </template>
          <template v-slot:item.valor_desconto="{ item }">
            {{ item.valor_desconto | moeda }}
          </template>
          <template v-slot:item.valor_total="{ item }">
            {{ item.valor_total | moeda }}
          </template>
        </v-data-table>
        <v-data-table
          class="rounded-lg"
          hide-default-footer
          no-data-text="Não há registro de vendas por forma de pagamento."
          loading-text="Buscando informações..."
          :loading="salesByPaymentMethodLoading"
          :headers="headersTotais"
          :header-props="{
            class: 'rounded-t-lg',
            color: $config.dados.corinputs,
          }"
          :items="allSalesByPaymentMethod.totals"
        >
          <template v-slot:item.valor="{ item }">
            {{ item.valor | moeda }}
          </template>
          <template v-slot:item.valor_juros="{ item }">
            {{ item.valor_juros | moeda }}
          </template>
          <template v-slot:item.valor_cancelado="{ item }">
            {{ item.valor_cancelado | moeda }}
          </template>
          <template v-slot:item.valor_desconto="{ item }">
            {{ item.valor_desconto | moeda }}
          </template>
          <template v-slot:item.valor_total="{ item }">
            {{ item.valor_total | moeda }}
          </template>
        </v-data-table>
      </CardModule>
    </AppBody>
  </fragment>
</template>
<script>
import dayjs from "dayjs";
import CardModule from "@/components/cards/CardModule.vue";
import BaseSelect from "@/components/forms/BaseSelect.vue";
import BaseTextField from "@/components/forms/BaseTextField.vue";
import BaseButton from "@/components/forms/BaseButton.vue";
import AppBody from "@/layouts/components/AppBody.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
export default {
  name: "SalesByPaymentMethod",
  metaInfo: {
    title:
      "Relatório Sintético de Vendas por Forma de Pagamento | ElevenTickets",
    meta: [
      {
        property: "og:title",
        content:
          "Relatório Sintético de Vendas por Forma de Pagamento | ElevenTickets",
      },
    ],
  },
  props: {},
  components: {
    CardModule,
    BaseSelect,
    BaseTextField,
    BaseButton,
    AppBody,
  },
  data() {
    return {
      filter: {
        paymentMethod: null,
        dateInterval: "",
        rangeDates: [],
      },
      actions: [
        {
          id: "CSV",
          name: "Exportar Excel/CSV",
          action: () => this.exportCSV(),
        },
        {
          id: "PDF",
          name: "Imprimir/Exportar PDF",
          action: () => this.exportPDF(),
        },
      ],
      dayjs: dayjs,
      activePicker: null,
      date: null,
      menu: false,
      placeholderData: "dd/mm/aa ~ dd/mm/aa",
      headers: [
        {
          text: "Forma de Pagamento",
          align: "center",
          sortable: false,
          value: "desc_forma_pag",
          width: 180,
        },
        {
          text: "Quant. de Pedidos",
          align: "center",
          value: "qtd_pedido",
          width: 180,
        },
        {
          text: "Quant. de Itens",
          align: "center",
          value: "qtd_itens_pedido",
          width: 200,
        },
        {
          text: "Valor",
          align: "center",
          value: "valor",
          width: 100,
        },
        {
          text: "Juros",
          align: "center",
          value: "valor_juros",
          width: 150,
        },
        {
          text: "Valor Cancelado",
          align: "center",
          value: "valor_cancelado",
          width: 150,
        },
        {
          text: "Descontos",
          align: "center",
          value: "valor_desconto",
          width: 200,
        },
        {
          text: "Total",
          align: "center",
          value: "valor_total",
          width: 150,
        },
      ],
      headersTotais: [
        {
          text: "Total de Pedidos",
          align: "center",
          sortable: false,
          value: "qtd_pedido",
          width: 160,
        },
        {
          text: "Total de Itens",
          align: "center",
          sortable: false,
          value: "qtd_itens_pedido",
          width: 200,
        },
        {
          text: "Valor",
          align: "center",
          sortable: false,
          value: "valor",
          width: 100,
        },
        {
          text: "Total de Juros",
          align: "center",
          sortable: false,
          value: "valor_juros",
          width: 150,
        },
        {
          text: "Total Cancelado",
          align: "center",
          sortable: false,
          value: "valor_cancelado",
          width: 150,
        },
        {
          text: "Total de Descontos",
          align: "center",
          sortable: false,
          value: "valor_desconto",
          width: 200,
        },
        {
          text: "Total",
          align: "center",
          sortable: false,
          value: "valor_total",
          width: 150,
        },
      ],
      vendasPorFormaPgtoHeader: [
        {
          title: "Forma de Pgto",
          size: "110px",
        },
        {
          title: "Quant. de Pedidos",
          size: "100px",
        },
        {
          title: "Quant. de Itens",
          size: "60px",
        },
        {
          title: "Valor",
          size: "80px",
        },
        {
          title: "Juros",
          size: "80px",
        },
        {
          title: "Descontos",
          size: "80px",
        },
        {
          title: "Total",
          size: "100px",
        },
      ],
      totaisHeader: [
        {
          title: "-",
          size: "110px",
        },
        {
          title: "Total de Pedidos",
          size: "100px",
        },
        {
          title: "Total de Itens",
          size: "60px",
        },
        {
          title: "Valor",
          size: "80px",
        },
        {
          title: "Total Juros",
          size: "80px",
        },
        {
          title: "Total de Descontos",
          size: "80px",
        },
        {
          title: "Total",
          size: "100px",
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      "storeSequencial",
      "intervalDates",
      "allSalesByPaymentMethod",
      "salesByPaymentMethodLoading",
      "exportCSVLoading",
      "exportPDFLoading",
      "paymentMethods",
    ]),
    computeRange: {
      get() {
        return this.filter.rangeDates;
      },
      set([firstDay]) {
        switch (this.filter.dateInterval) {
          case "date": {
            // const day = new Date(new Date(firstDay).setDate(new Date(firstDay).getDate())).toISOString().slice(0, 10);
            this.filter.rangeDates = [firstDay, firstDay];
            break;
          }
          case "week": {
            const currentDayWeek = this.dayjs(firstDay)
              .day(0)
              .format("YYYY-MM-DD");
            const week = this.dayjs(firstDay).day(6).format("YYYY-MM-DD");
            this.filter.rangeDates = [currentDayWeek, week];
            break;
          }
          case "month": {
            const currentDayMonth = this.dayjs(firstDay)
              .startOf("month")
              .format("YYYY-MM-DD");
            const month = this.dayjs(firstDay)
              .endOf("month")
              .format("YYYY-MM-DD");
            this.filter.rangeDates = [currentDayMonth, month];
            break;
          }
          case "custom": {
            const currentDayCustom = new Date(
              Date.now() - new Date().getTimezoneOffset() * 60000
            )
              .toISOString()
              .substr(0, 10);
            const custom = "";
            this.filter.rangeDates = [currentDayCustom, custom];
            break;
          }
          default: {
            const defaultDay = "";
            this.filter.rangeDates = [firstDay, defaultDay];
            break;
          }
        }
      },
    },
    dateRangeText() {
      let arrayDates = [];
      this.filter.rangeDates.forEach(item => {
        return arrayDates.push(this.$config.convertDataBR(item));
      });

      return arrayDates.join(" ~ ");
    },
    isNotCustomRangeDates() {
      return this.filter.dateInterval !== "custom";
    },
    disabledDatePicker() {
      return this.filter.dateInterval != null ? false : true;
    },
  },
  watch: {
    "filter.dateInterval"(e) {
      switch (e) {
        case "date": {
          const currentDay = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000
          )
            .toISOString()
            .substr(0, 10);
          // const day = new Date(new Date(currentDay).setDate(new Date(currentDay).getDate() + 1)).toISOString().slice(0, 10);
          this.filter.rangeDates = [currentDay, currentDay];
          break;
        }
        case "week": {
          const currentDayWeek = this.dayjs().day(0).format("YYYY-MM-DD");
          const week = this.dayjs().day(6).format("YYYY-MM-DD");
          this.filter.rangeDates = [currentDayWeek, week];
          break;
        }
        case "month": {
          const currentDayMonth = this.dayjs()
            .startOf("month")
            .format("YYYY-MM-DD");
          const month = this.dayjs().endOf("month").format("YYYY-MM-DD");
          this.filter.rangeDates = [currentDayMonth, month];
          break;
        }
        case "custom": {
          const currentDayCustom = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000
          )
            .toISOString()
            .substr(0, 10);
          const custom = new Date(
            new Date(currentDayCustom).setDate(
              new Date(currentDayCustom).getDate() + 1
            )
          )
            .toISOString()
            .slice(0, 10);
          this.filter.rangeDates = [currentDayCustom, custom];
          break;
        }
        default: {
          const defaultDay = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000
          )
            .toISOString()
            .substr(0, 10);
          const defaultDayCustom = new Date(
            new Date(defaultDay).setDate(new Date(defaultDay).getDate() + 1)
          )
            .toISOString()
            .slice(0, 10);
          this.filter.rangeDates = [defaultDay, defaultDayCustom];
          break;
        }
      }
    },
    menu(val) {
      val &&
        setTimeout(() => {
          if (val && this.filter.dateInterval == "date") {
            this.activePicker = "DATE";
          } else if (val && this.filter.dateInterval == "week") {
            this.activePicker = "DATE";
          } else if (val && this.filter.dateInterval == "month") {
            this.activePicker = "MONTH";
          } else if (val && this.filter.dateInterval == "custom") {
            this.activePicker = "DATE";
          } else {
            this.activePicker = "DATE";
          }
        });
    },
  },
  methods: {
    ...mapActions([
      "fetchSalesByPaymentMethod",
      "exportPaymentMethodCSV",
      "exportPaymentMethodPDF",
      "fetchPaymentMethodOptions",
    ]),
    ...mapMutations(["setSalesByPaymentMethodLoading"]),
    filterPaymentMethod() {
      this.setSalesByPaymentMethodLoading(true);
      let filter = {};

      if (this.filter.rangeDates.length > 0) {
        Object.assign(filter, {
          producerId: this.storeSequencial,
          forma_pagamento: this.filter.paymentMethod,
          dthr_ped_ini: `${this.filter.rangeDates[0]} 00:00`,
          dthr_ped_fim: `${this.filter.rangeDates[1]} 23:59`,
        });
      } else {
        Object.assign(filter, {
          producerId: this.storeSequencial,
          forma_pagamento: this.filter.paymentMethod,
        });
      }
      this.fetchSalesByPaymentMethod(filter);
    },
    metodoPagamentoExibicao(item) {
      if (item === null) {
        return "-";
      } else if (item === "QRCODE") {
        return "PIX";
      } else {
        return item;
      }
    },
    replaceSpecialChars(str) {
      str = str.replace(/[ÀÁÂÃÄÅ]/, "A");
      str = str.replace(/[àáâãäå]/, "a");
      str = str.replace(/[ÈÉÊË]/, "E");
      str = str.replace(/[Ç]/, "C");
      str = str.replace(/[ç]/, "c");
      // o resto
      return str.replace(/[^a-z0-9]/gi, "");
    },
    monthEvent(month) {
      const currentDayMonth = this.dayjs(month)
        .startOf("month")
        .format("YYYY-MM-DD");
      const monthEnd = this.dayjs(month).endOf("month").format("YYYY-MM-DD");
      this.filter.rangeDates = [currentDayMonth, monthEnd];
    },
    exportCSV() {
      let filter = {};

      if (this.filter.rangeDates.length > 0) {
        Object.assign(filter, {
          producerId: this.storeSequencial,
          dthr_ped_ini: `${this.filter.rangeDates[0]} 00:00`,
          dthr_ped_fim: `${this.filter.rangeDates[1]} 23:59`,
        });
      } else {
        Object.assign(filter, {
          producerId: this.storeSequencial,
        });
      }
      this.exportPaymentMethodCSV(filter);
    },
    exportPDF() {
      let filter = {};

      if (this.filter.rangeDates.length > 0) {
        Object.assign(filter, {
          producerId: this.storeSequencial,
          dthr_ped_ini: `${this.filter.rangeDates[0]} 00:00`,
          dthr_ped_fim: `${this.filter.rangeDates[1]} 23:59`,
        });
      } else {
        Object.assign(filter, {
          producerId: this.storeSequencial,
        });
      }
      this.exportPaymentMethodPDF(filter);
    },
  },
  mounted() {
    this.fetchPaymentMethodOptions();
    this.fetchSalesByPaymentMethod({ producerId: this.storeSequencial });
  },
  destroyed() {
    this.filter = {
      paymentMethod: null,
      dateInterval: "",
      rangeDates: [],
    };
  },
};
</script>
<style scoped></style>
